import type {
  SavePriceSettingsRequestBody,
  SimulationStatus,
  StartSimulationRequestBody,
  UpdateSimulationAssignmentRequestBody,
} from '@/api';
import { getApiClient } from '@/api/client';
import type { SavePriceRowsRequestBody } from '@/api/models/SavePriceRowsRequestBody';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { type NormalizedError, normalizeApiError } from '@/utils/error-utils';
import { useMutation, useQuery } from '@tanstack/react-query';

export const SIMULATION_LIST_QUERY_KEY = 'simulationList';

export const useSimulationDetails = (simulationId: number) =>
  useQuery({
    queryKey: ['simulation', simulationId],
    queryFn: async () =>
      getApiClient().simulationSimulationId({ simulationId }),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: !!simulationId,
    retry: false,
  });

export const useSimulationData = (simulationId: number, enabled = true) =>
  useQuery({
    queryKey: ['simulationData', simulationId],
    queryFn: async () => getApiClient().getDetailedSimulation({ simulationId }),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
    enabled,
  });

export const usePricelistSettings = (simulationId: number, enabled = true) =>
  useQuery({
    queryKey: ['priceSettings', simulationId],
    queryFn: async () => getApiClient().getPriceSettings({ simulationId }),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
    enabled,
  });

export const useKeyFiguresPreviousYear = (simulationId: number) =>
  useQuery({
    queryKey: ['keyFiguresPreviousYear', simulationId],
    queryFn: async () =>
      getApiClient().getKeyFiguresPreviousYear({ simulationId }),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
    enabled: !!simulationId,
  });

export const useSavePrices = (
  simulationId: number,
  onSuccess: () => void,
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: async (request: SavePriceRowsRequestBody) =>
      getApiClient().savePriceRows({
        simulationId,
        savePriceRowsRequestBody: request,
      }),
    onSuccess,
    onError: (err) => onError(normalizeApiError(err)),
  });

export const useSavePricelistSettings = (
  simulationId: number,
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: async (request: SavePriceSettingsRequestBody) =>
      getApiClient().savePriceSettings({
        simulationId,
        savePriceSettingsRequestBody: request,
      }),
    onError: (err) => onError(normalizeApiError(err)),
  });

export const useStartSimulation = (
  onSuccess: (simulationId: number) => void,
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: async (request: StartSimulationRequestBody) =>
      getApiClient()
        .simulationStart({ startSimulationRequestBody: request })
        .then((r) => r.simulationId),
    onSuccess: async (simulationId) => onSuccess(simulationId),
    onError: (err) => onError(normalizeApiError(err)),
  });

export const usePublishSimulation = (
  onSuccess: () => void,
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: ({
      simulationId,
      confirmed,
    }: { simulationId: number; confirmed?: boolean }) =>
      getApiClient().simulationPublishPricesSimulationId({
        simulationId,
        publishPricesRequestBody: confirmed ? { confirmed } : undefined,
      }),
    onSuccess,
    onError: (err) => onError(normalizeApiError(err)),
  });

export const useSimulationList = () =>
  useQuery({
    queryKey: [SIMULATION_LIST_QUERY_KEY],
    queryFn: async () => {
      return getApiClient().simulationList();
    },
  });

export const useDeleteSimulation = (
  onSuccess: () => void,
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: async (simulationIds: number[]) =>
      getApiClient().simulationDelete({ requestBody: simulationIds }),
    onSuccess,
    onError: (err) => onError(normalizeApiError(err)),
  });
export const useRenameSimulation = (
  onSuccess: () => void,
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: async (request: { simulationId: number; newName: string }) =>
      getApiClient().renameSimulation({
        simulationId: request.simulationId,
        renameSimulationRequestBody: { simulationName: request.newName },
      }),
    onSuccess,
    onError: (err) => onError(normalizeApiError(err)),
  });

export const useSimulationVersions = (simulationId: number, enabled = true) =>
  useQuery({
    queryKey: ['simulationVersions', simulationId],
    queryFn: async () => getApiClient().getVersions({ simulationId }),
    enabled: !!simulationId && enabled,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

export const useSimulationWatchers = (simulationId: number, enabled = true) =>
  useQuery({
    queryKey: ['simulationWatchers', simulationId],
    queryFn: async () => getApiClient().getWatchers({ simulationId }),
    enabled: !!simulationId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

export const useAssigneeCandidates = (
  simulationId: number,
  targetStatus: SimulationStatus,
  searchValue: string,
  enabled = true,
) => {
  const debouncedSearch = useDebouncedValue(searchValue.trim(), 300);
  return useQuery({
    queryKey: [
      'assigneeCandidates',
      simulationId,
      targetStatus,
      debouncedSearch,
    ],
    queryFn: () =>
      getApiClient().getAssigneeCandidates({
        simulationId,
        targetStatus,
        query: debouncedSearch,
      }),
    enabled:
      !!simulationId &&
      enabled &&
      debouncedSearch.length >= 2 &&
      debouncedSearch.length <= 200,
    retry: false,
  });
};

export const useReviewerCandidates = (
  simulationId: number,
  simulationStatus: SimulationStatus,
  searchValue: string,
  enabled = true,
) => {
  const debouncedSearch = useDebouncedValue(searchValue.trim(), 300);
  return useQuery({
    queryKey: [
      'reviewerCandidates',
      simulationId,
      simulationStatus,
      debouncedSearch,
    ],
    queryFn: () =>
      getApiClient().getReviewerCandidates({
        simulationId,
        query: debouncedSearch,
      }),
    enabled:
      !!simulationId &&
      enabled &&
      debouncedSearch.length >= 2 &&
      debouncedSearch.length <= 200,
    retry: false,
  });
};

export const useWatcherCandidates = (
  simulationId: number,
  searchValue: string,
  enabled = true,
) => {
  const debouncedSearch = useDebouncedValue(searchValue.trim(), 300);
  return useQuery({
    queryKey: ['watcherCandidates', simulationId, debouncedSearch],
    queryFn: () =>
      getApiClient().getWatcherCandidates({
        simulationId,
        query: debouncedSearch,
      }),
    enabled:
      !!simulationId &&
      enabled &&
      debouncedSearch.length >= 2 &&
      debouncedSearch.length <= 200,
    retry: false,
  });
};

export const usePreviewSimulationVersion = (
  simulationId: number,
  versionId: number | null,
) =>
  useQuery({
    queryKey: ['versionPreview', simulationId, versionId],
    queryFn: async () =>
      getApiClient().previewVersionAsSimulation({
        simulationId,
        versionId: versionId as number,
      }),
    enabled: versionId !== null,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

export const useVersionPricelistSettings = (
  simulationId: number,
  versionId: number | null,
) =>
  useQuery({
    queryKey: ['versionPriceSettings', simulationId, versionId],
    queryFn: async () =>
      getApiClient().getVersionPriceSettings({
        simulationId,
        versionId: versionId as number,
      }),
    enabled: versionId !== null,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

export const useUpdateSimulationAssignment = (
  onSuccess: () => void,
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: async ({
      simulationId,
      body,
    }: { simulationId: number; body: UpdateSimulationAssignmentRequestBody }) =>
      getApiClient().updateSimulationAssignment({
        simulationId,
        updateSimulationAssignmentRequestBody: body,
      }),
    onSuccess,
    onError: (err) => onError(normalizeApiError(err)),
  });

export type DownloadedFile = {
  blob: Blob;
  fileName: string;
};

/** Extracts the file name from a `Content-Disposition: attachment; filename="..."` header. */
const extractFileNameFromContentDisposition = (
  header: string | null,
): string | null => {
  if (!header) return null;
  const match = header.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

export const useDownloadSimulationExcelMismatches = (
  onError: (error: NormalizedError) => void,
) =>
  useMutation({
    mutationFn: async (prefix: string): Promise<DownloadedFile> => {
      const response =
        await getApiClient().downloadSimulationExcelMismatchesRaw({
          prefix,
        });
      const blob = await response.raw.blob();
      const fileName =
        extractFileNameFromContentDisposition(
          response.raw.headers.get('content-disposition'),
        ) ?? `Simulation Mismatches ${prefix}.xlsx`;
      return { blob, fileName };
    },
    onError: (err) => onError(normalizeApiError(err)),
  });

export const useUpdateWatchers = (onError: (error: NormalizedError) => void) =>
  useMutation({
    mutationFn: async ({
      simulationId,
      userIds,
    }: { simulationId: number; userIds: string[] }) =>
      getApiClient().updateWatchers({
        simulationId,
        updateWatchersRequestBody: { userIds },
      }),
    onError: (err) => onError(normalizeApiError(err)),
  });
